import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { interval, Subject } from 'rxjs';
import { filter, finalize, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { AuthService } from '../../../services/auth/auth.service';
import { DemoSeedApiService } from './demo-seed-api.service';
import {
  DemoSeedConfig,
  DemoSeedContextResponse,
  DemoSeedPathContext,
  EventTemplateConfig,
  PathAllocationRule,
} from './demo-seed.models';

@Component({
  selector: 'app-duna-cprng-seeder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatProgressBarModule,
  ],
  templateUrl: './duna-cprng-seeder.component.html',
  styleUrl: './duna-cprng-seeder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaCprngSeederComponent implements OnInit, OnDestroy {
  allowed = false;
  isProduction = environment.production;
  loading = false;
  errorMessage: string | null = null;

  context: DemoSeedContextResponse | null = null;
  config: DemoSeedConfig = {};
  eventTypeExercise = true;
  eventTypeEducation = false;
  eventTypeCourse = false;

  previewResult: string | null = null;
  runResult: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private api: DemoSeedApiService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((roles) => {
        const isSuper =
          (roles || []).map((r) => r.toLowerCase()).includes('superadmin');
        this.allowed = isSuper && !this.isProduction;
        this.cdr.markForCheck();
        if (this.allowed) {
          this.loadContext();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadContext(): void {
    this.loading = true;
    this.errorMessage = null;
    this.api
      .getContext()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (ctx) => {
          this.context = ctx;
          this.applyDefaults(ctx);
        },
        error: () => {
          this.errorMessage = 'Kunne ikke hente demo-seed kontekst.';
        },
      });
  }

  private applyDefaults(ctx: DemoSeedContextResponse): void {
    this.config = { ...(ctx.defaults ?? {}) };
    if (!this.config.pathAllocationRules?.length) {
      const rules = ctx.file_defaults?.['defaultPathAllocationRules'];
      if (Array.isArray(rules)) {
        this.config.pathAllocationRules = rules as PathAllocationRule[];
      }
    }
    if (!this.config.pathMaintenanceSpecs?.length) {
      const specs = ctx.file_defaults?.['pathMaintenanceHints'];
      if (Array.isArray(specs)) {
        this.config.pathMaintenanceSpecs = specs as DemoSeedConfig['pathMaintenanceSpecs'];
      }
    }
    if (!this.config.eventTemplates?.length) {
      const tpl = ctx.file_defaults?.['eventTemplates'];
      if (Array.isArray(tpl)) {
        this.config.eventTemplates = tpl as EventTemplateConfig[];
      }
    }
    if (!this.config.modules?.length) {
      const mods = ctx.file_defaults?.['modules'];
      if (Array.isArray(mods)) {
        this.config.modules = mods as DemoSeedConfig['modules'];
      }
    }
    const types = this.config.eventTypes ?? ['exercise'];
    this.eventTypeExercise = types.includes('exercise');
    this.eventTypeEducation = types.includes('education');
    this.eventTypeCourse = types.includes('course');
    this.applyPathPresets();
  }

  buildPayload(): DemoSeedConfig {
    const eventTypes: string[] = [];
    if (this.eventTypeExercise) {
      eventTypes.push('exercise');
    }
    if (this.eventTypeEducation) {
      eventTypes.push('education');
    }
    if (this.eventTypeCourse) {
      eventTypes.push('course');
    }
    return {
      ...this.config,
      eventTypes,
    };
  }

  applyPathPresets(): void {
    if (!this.context?.paths?.length) {
      return;
    }
    const normalize = (value: string): string =>
      value.trim().toLowerCase().replace(/\s+/g, ' ');
    const labelsFor = (p: DemoSeedPathContext): string[] => {
      const out: string[] = [];
      const name = normalize(p.name ?? '');
      if (name) {
        out.push(name);
      }
      const full = normalize(p.full_name ?? '');
      if (full && !out.includes(full)) {
        out.push(full);
      }
      return out;
    };
    const findGuid = (needle: string, exclude?: string): string | undefined => {
      const n = normalize(needle);
      const ex = exclude ? normalize(exclude) : '';
      const match = this.context!.paths.find((p) => {
        if (!p.v1_valid) {
          return false;
        }
        return labelsFor(p).some((label) => label === n && (!ex || label !== ex));
      });
      return match?.guid;
    };
    const fui = findGuid('fui');
    const hlTl = findGuid('hl tl');
    const hl = findGuid('hl', 'hl tl');
    const isl = findGuid('isl');
    const rules: PathAllocationRule[] = [];
    if (fui) {
      rules.push({ pathGuids: [fui], userPercent: 100, grouped: false });
    }
    if (hlTl && hl) {
      rules.push({
        pathGuids: [hl, hlTl],
        userPercent: 20,
        grouped: true,
        groupKey: 'hl',
      });
    } else if (hl) {
      rules.push({ pathGuids: [hl], userPercent: 20, grouped: false });
    }
    if (isl) {
      rules.push({ pathGuids: [isl], userPercent: 5, grouped: false });
    }
    if (rules.length > 0) {
      this.config.pathAllocationRules = rules;
    }
    this.cdr.markForCheck();
  }

  preview(): void {
    this.loading = true;
    this.previewResult = null;
    this.api
      .preview(this.buildPayload())
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.previewResult = JSON.stringify(res, null, 2);
          if (!res.ok) {
            this.snackBar.open('Preview: preflight fejl — se detaljer', 'OK', {
              duration: 5000,
            });
          }
        },
        error: (err) => {
          const msg = this.apiErrorMessage(err);
          this.errorMessage = msg;
          this.previewResult = JSON.stringify(err.error ?? { message: msg }, null, 2);
          this.snackBar.open(msg, 'OK', { duration: 8000 });
        },
      });
  }

  runSeed(): void {
    if (
      !confirm(
        'Dette rydder tidligere demo-seed på org 11 og opretter nyt dataset. Fortsæt?'
      )
    ) {
      return;
    }
    this.loading = true;
    this.runResult = null;
    this.errorMessage = null;
    this.api
      .run(this.buildPayload())
      .pipe(
        finalize(() => {
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          if (res.async && res.status === 'running' && res.run_id) {
            this.errorMessage = null;
            this.runResult = JSON.stringify(res, null, 2);
            this.snackBar.open('Demo seed kører — venter på resultat…', 'OK', {
              duration: 4000,
            });
            this.pollRunUntilDone(res.run_id);
            return;
          }
          this.loading = false;
          this.errorMessage = null;
          this.runResult = JSON.stringify(res, null, 2);
          this.snackBar.open('Demo seed fuldført', 'OK', { duration: 4000 });
          this.loadContext();
        },
        error: (err) => {
          this.loading = false;
          const msg = this.apiErrorMessage(err);
          this.errorMessage = msg;
          this.runResult = JSON.stringify(err.error ?? { message: msg }, null, 2);
          this.snackBar.open(msg, 'OK', { duration: 12000 });
        },
      });
  }

  private pollRunUntilDone(runId: string): void {
    const maxPolls = 180;
    let sawTerminal = false;
    interval(2000)
      .pipe(
        startWith(0),
        take(maxPolls),
        switchMap(() => this.api.getContext()),
        takeUntil(this.destroy$),
        filter((ctx) => {
          const lr = ctx.last_run;
          return lr != null && lr.run_id === runId && lr.status !== 'running';
        }),
        take(1),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (ctx) => {
          sawTerminal = true;
          this.context = ctx;
          const lr = ctx.last_run!;
          if (lr.status === 'success') {
            this.errorMessage = null;
            this.runResult = JSON.stringify(
              { ok: true, run_id: lr.run_id, status: lr.status, stats: lr.stats },
              null,
              2
            );
            this.snackBar.open('Demo seed fuldført', 'OK', { duration: 4000 });
            return;
          }
          const msg =
            lr.error_message?.trim() ||
            `Demo seed fejlede (status: ${lr.status}).`;
          this.errorMessage = msg;
          this.runResult = JSON.stringify(lr, null, 2);
          this.snackBar.open(msg, 'OK', { duration: 12000 });
        },
        complete: () => {
          if (!sawTerminal) {
            const msg =
              'Seed kører stadig — opdater siden eller tjek «Sidste kørsel».';
            this.errorMessage = msg;
            this.snackBar.open(msg, 'OK', { duration: 10000 });
            this.loadContext();
          }
        },
        error: () => {
          this.errorMessage = 'Kunne ikke hente status for demo-seed.';
        },
      });
  }

  cleanup(): void {
    if (!confirm('Ryd al demo-seed data på org 11?')) {
      return;
    }
    this.loading = true;
    this.api
      .cleanup()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadContext();
        })
      )
      .subscribe({
        next: (res) => {
          this.runResult = JSON.stringify(res, null, 2);
          this.snackBar.open('Oprydning fuldført', 'OK', { duration: 4000 });
        },
        error: (err) => {
          const msg = this.apiErrorMessage(err);
          this.errorMessage = msg;
          this.snackBar.open(msg, 'OK', { duration: 8000 });
        },
      });
  }

  resetRunning(): void {
    this.loading = true;
    this.api
      .resetRunning()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadContext();
        })
      )
      .subscribe({
        next: (res) => {
          this.snackBar.open(
            res.count > 0
              ? `Nulstillede ${res.count} hængende kørsel(er)`
              : 'Ingen hængende kørsel at nulstille',
            'OK',
            { duration: 5000 }
          );
        },
        error: (err) => {
          this.snackBar.open(this.apiErrorMessage(err), 'OK', { duration: 8000 });
        },
      });
  }

  pathLabel(guid: string): string {
    const p = this.context?.paths?.find((x) => x.guid === guid);
    return p?.name ?? p?.full_name ?? guid;
  }

  /** Paths that seed actually touches (from rules + maintenance specs). */
  seedScopeNames(): string[] {
    const fromContext = this.context?.seed_scope_paths;
    if (fromContext?.length) {
      return fromContext.map((p) => p.name ?? p.full_name ?? p.guid).filter(Boolean);
    }
    const guids = new Set<string>();
    for (const rule of this.config.pathAllocationRules ?? []) {
      rule.pathGuids?.forEach((g) => guids.add(g));
    }
    return [...guids].map((g) => this.pathLabel(g));
  }

  seedScopeCount(): number {
    return this.seedScopeNames().length;
  }

  private apiErrorMessage(err: unknown): string {
    if (err && typeof err === 'object') {
      const http = err as {
        error?: unknown;
        message?: string;
        status?: number;
        statusText?: string;
      };
      if (typeof http.error === 'string') {
        const trimmed = http.error.trim();
        if (trimmed.startsWith('<')) {
          return `Server returnerede HTML (status ${http.status ?? '?'}), ikke JSON — typisk timeout/proxy-feil.`;
        }
        if (trimmed.length > 0 && trimmed.length < 500) {
          return trimmed;
        }
      }
      if (http.error && typeof http.error === 'object' && 'message' in http.error) {
        const message = (http.error as { message: unknown }).message;
        if (typeof message === 'string' && message.trim() !== '') {
          return message;
        }
      }
      if (http.status === 0) {
        return 'Ingen svar fra API (netværk/timeout).';
      }
      if (http.statusText) {
        return `${http.statusText} (${http.status ?? '?'})`;
      }
      if (http.message) {
        return http.message;
      }
    }
    return 'Ukendt API-fejl';
  }
}
